import type { FC, ReactNode } from 'react'
import type { CartProduct } from '../../shopping-cart/provider/types'
import type { GetShopItems_getShopItems } from '@g4g/graphql/src/shop/__generated__/GetShopItems'
import {
  PurchaseCategory,
  PurchaseType,
  Rarity,
} from '@g4g/graphql/src/shop/__generated__/globalTypes'
import RarityTag from '../RarityTag'
import { classNames } from '@g4g/utils/src/react/class-names'
import { simplifiedProductId } from '@/shop/src/utils/shopItemUtils'

type ConsumableLabel = 'Rarity' | 'Quantity'

const BundleWrapper: FC<{
  children?: ReactNode
  shopItem: GetShopItems_getShopItems
  variant: 'cart' | 'history'
}> = ({ children, shopItem, variant }) => (
  <>
    {children && (
      <>
        {variant === 'history' && (
          <div className="flex space-x-4 text-sm">
            <span className="text-white/50">Id:</span>
            <span className="text-white/90">{simplifiedProductId(shopItem)}</span>
          </div>
        )}
        <>{children}</>
      </>
    )}
  </>
)

const ConsumableWrapper: FC<{
  label?: ConsumableLabel
  value: JSX.Element
  children?: ReactNode
  variant: 'cart' | 'history'
}> = ({ label, children, value, variant }) => (
  <>
    <div
      className={classNames(
        label === 'Rarity' ? 'sm:space-x-4' : 'space-x-4',
        'flex items-center my-1  text-sm text-white/90'
      )}
      data-cy="shop-cart-product-qty"
    >
      {label && (
        <>
          {label === 'Quantity' ? (
            <span>
              <span className={classNames('sm:hidden', variant === 'history' && 'text-white/50')}>
                {'Qty:'}
              </span>
              <span
                className={classNames(
                  'sr-only sm:not-sr-only',
                  variant === 'history' && 'text-white/50'
                )}
              >
                {label + ':'}
              </span>
            </span>
          ) : (
            <span
              className={classNames(
                label === 'Rarity' && 'sr-only sm:not-sr-only',
                variant === 'history' && 'text-white/50'
              )}
            >
              {label + ':'}
            </span>
          )}
        </>
      )}
      {/* Note: bit sketchy. Adding children like {x && (<>something</>)} breaks this conditional. */}
      <>{variant !== 'history' ? <>{children ? <>{children}</> : value}</> : <>{value}</>}</>
    </div>
    {variant === 'history' && <>{children}</>}
  </>
)

const ProductEdit: FC<{
  product: CartProduct
  children?: ReactNode
  variant?: 'cart' | 'history'
}> = ({ product, children, variant = 'cart' }) => {
  if (product.product.purchaseType === PurchaseType.Subscription) {
    return (
      <ConsumableWrapper
        variant={variant}
        label="Rarity"
        value={<RarityTag size={20} rarity={product.product.subscription?.rarity ?? Rarity.Rare} />}
      >
        {children}
      </ConsumableWrapper>
    )
  }

  return (
    <>
      {product.product.purchaseCategory === PurchaseCategory.Bundle ? (
        <BundleWrapper variant={variant} shopItem={product.product}>
          {children}
        </BundleWrapper>
      ) : (
        <ConsumableWrapper
          variant={variant}
          label="Quantity"
          value={<span>{product.totalQuantity}</span>}
        >
          {children}
        </ConsumableWrapper>
      )}
    </>
  )
}

export default ProductEdit
